import { useEffect, useRef } from 'react';

import { DocumentReaderRunState } from '@commenda/api/commenda-ai/document-reader/types';
import { useDocumentReaderRun } from '@commenda/api/commenda-ai/document-reader/useDocumentReaderRun';
import { toast } from '@commenda/kakashi';

import { useCommendaAiActions } from '../CommendaAiActionsContext';
import { useDocumentReaderChatRefresh } from './useDocumentReaderChatRefresh';

/** What follows one docked run whether or not its strip shows: the reader's finish messages brought into
 * the open chat, and one more look at a finished run after each turn, which may have started a re-read or
 * a write-back on it. */
export function useDocumentReadingRunFinishWatch(
  runId: string,
  isNewInSession: boolean
): {
  isLoading: boolean;
  error: unknown;
} {
  const { run, refreshRun } = useDocumentReaderRun(runId);
  const actions = useCommendaAiActions();
  const isGenerating = actions?.isGenerating ?? false;

  const { isLoading, error } = useDocumentReaderChatRefresh(
    {
      runId,
      runState: run?.state,
      writeBackProposalId: run?.writeBack?.proposalId ?? null,
      writeBackState: run?.writeBack?.state ?? null,
      reExtractState: run?.reExtract?.state ?? null,
    },
    isNewInSession
  );
  useEffect(() => {
    if (error)
      toast.error(
        'Could not show the finish message. Reload the chat to see it.'
      );
  }, [error]);

  // A finished run is no longer polled, so a turn that may have started a re-read or write-back re-reads it once.
  const wasGeneratingRef = useRef(isGenerating);
  useEffect(() => {
    if (
      wasGeneratingRef.current &&
      !isGenerating &&
      run?.state === DocumentReaderRunState.COMPLETED
    )
      refreshRun().catch(() => undefined);
    wasGeneratingRef.current = isGenerating;
  }, [isGenerating, run?.state, refreshRun]);

  return { isLoading, error };
}
